import { useState } from 'react';
import { TextKey } from '@mews-commander/enums';
import { AvailabilityBlock, CancellationReason } from '@mews-commander/data-access';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { Grid, Link, Highlight, Stack, Textarea } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { Option } from '@mews-commander/types';
import { Select } from '@mews-commander/ui';
import { DashboardCard } from './DashboardCard';
import { RevertCancellationButton } from './buttons/RevertCancellationButton';

interface CandellationReasonDialogFieldsProps {
    cancellationReason: CancellationReason;
    cancellationReasonDetail: string | undefined;
}
const CandellationReasonDialogFields = ({
    cancellationReason,
    cancellationReasonDetail,
}: CandellationReasonDialogFieldsProps) => (
    <Stack spacing="200" vertical>
        <Select
            label={<LocalizedText textKey={TextKey.CancellationReason} />}
            options={[
                {
                    label: <LocalizedEnum prefix="CancellationReason" value={cancellationReason} />,
                    value: cancellationReason,
                } as Option,
            ]}
            selected={cancellationReason}
            required
            disabled
        />
        <Textarea
            name="cancellationReasonDetail"
            label={<LocalizedText textKey={TextKey.DetailPlural} />}
            value={cancellationReasonDetail ?? undefined}
            disabled
        />
    </Stack>
);

interface CancellationReasonCardProps {
    availabilityBlock: AvailabilityBlock;
    available: number;
}
export const CancellationReasonCard = ({ availabilityBlock, available }: CancellationReasonCardProps) => {
    const { cancellationReason, cancellationReasonDetail } = availabilityBlock;
    const [opened, setOpened] = useState(false);

    const localizedCancellationReason = cancellationReason ? (
        <LocalizedEnum prefix="CancellationReason" value={cancellationReason} />
    ) : null;
    return (
        <>
            <Grid templateColumns="1fr 1fr">
                <DashboardCard
                    variant="subtle"
                    textKey={TextKey.BlocksCanceled}
                    content={<Highlight size="small">{available}</Highlight>}
                    button={<RevertCancellationButton availabilityBlock={availabilityBlock} />}
                    data-test-canceled={available}
                />
                <DashboardCard
                    variant="subtle"
                    textKey={TextKey.CancellationReason}
                    content={
                        cancellationReasonDetail ? (
                            <Link inherit onClick={() => setOpened(true)}>
                                {localizedCancellationReason}
                            </Link>
                        ) : (
                            <span>{localizedCancellationReason}</span>
                        )
                    }
                    data-test-cancellation-reason
                />
            </Grid>

            <FloatingPanel
                active={opened}
                size="small"
                title={<LocalizedText textKey={TextKey.CancellationReason} />}
                onClose={() => setOpened(false)}
                loading={false}
            >
                {cancellationReason && (
                    <CandellationReasonDialogFields
                        cancellationReason={cancellationReason}
                        cancellationReasonDetail={cancellationReasonDetail ?? undefined}
                    />
                )}
            </FloatingPanel>
        </>
    );
};
